// ============================================================
// FinishedStockTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   Finished rolls in stock, split into two kinds:
//
//   OPEN      plain rolls, sellable to anyone
//   LOCKED    printed with a customer's name, so they can only
//             ever go to that customer
//
// WHY THE SPLIT MATTERS
// Showing both as one number would tell the owner he has 300
// rolls to sell when 200 belong to somebody else. Open stock is
// what he can actually trade; locked stock is a delivery waiting
// to happen.
//
// Stock is written by production runs and will be reduced by
// sales. Nothing on this screen changes it — this is a view.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. FinishedStockTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Package, Search, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
  purple:     '#7c3aed',
  purpleBg:   '#ede9fe',
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, colour = C.text, sub }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{sub}</div>}
  </div>
);

const KindBadge = ({ locked }) => (
  <span style={{
    fontSize: '12px', fontWeight: '600', padding: '3px 11px',
    borderRadius: '11px', whiteSpace: 'nowrap',
    background: locked ? C.purpleBg : C.infoBg,
    color:      locked ? C.purple   : C.info,
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  }}>
    {locked ? <Lock size={11} /> : <Package size={11} />}
    {locked ? 'Reserved' : 'Open'}
  </span>
);


// ============================================================
// B. MAIN COMPONENT
// ============================================================

const FinishedStockTab = () => {
  const [stock, setStock]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('');
  const [search, setSearch]   = useState('');
  const [banner, setBanner]   = useState({ type: '', message: '' });
  const [editingPrice, setEditingPrice] = useState(null); // { id, price }

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const params = kindFilter ? `?kind=${kindFilter}` : '';
      const res = await axios.get(`${API}/tape/production-runs/stock${params}`, { headers });
      setStock(res.data.data.stock || []);
      setSummary(res.data.data.summary || null);
    } catch {
      notify('error', 'Could not load stock. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [kindFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  const savePrice = async (productId, newPrice) => {
    try {
      await axios.put(
        `${API}/tape/sales/products/${productId}/price`,
        { default_price: newPrice },
        { headers }
      );
      setEditingPrice(null);
      notify('success', 'Price updated');
      load();
    } catch {
      notify('error', 'Failed to update price');
    }
  };

  // Filtering on the loaded list rather than the server — the
  // number of product/customer combinations stays small.
  const term = search.trim().toLowerCase();
  const visible = term
    ? stock.filter(s =>
        (s.product_name || '').toLowerCase().includes(term) ||
        (s.customer_name || '').toLowerCase().includes(term) ||
        (s.print_design || '').toLowerCase().includes(term))
    : stock;

  // Value of open stock only. Reserved rolls are already
  // spoken for, so counting them as sellable value would
  // overstate what the business can actually turn into cash.
  const openValue = stock
    .filter(s => !s.customer_id)
    .reduce((sum, s) => sum + (Number(s.quantity) * Number(s.default_price || 0)), 0);

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        <StatCard
          label="Open stock"
          value={summary?.open_rolls ?? '—'}
          colour={C.info}
          sub="sellable to anyone"
        />
        <StatCard
          label="Reserved"
          value={summary?.locked_rolls ?? '—'}
          colour={C.purple}
          sub={summary ? `for ${summary.customers_holding} customer(s)` : undefined}
        />
        <StatCard
          label="Total rolls"
          value={summary?.total_rolls ?? '—'}
        />
        <StatCard
          label="Open stock value"
          value={openValue > 0 ? money(openValue) : '—'}
          sub={openValue > 0 ? 'at default prices' : 'no prices set yet'}
        />
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Finished stock
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Printed rolls are reserved for the customer they were made for.
            </p>
          </div>

          <div style={{ position: 'relative', minWidth: '190px' }}>
            <Search
              size={15}
              style={{
                position: 'absolute', left: '12px', top: '50%',
                transform: 'translateY(-50%)', color: C.textMuted,
              }}
            />
            <input
              style={{ ...inputStyle, paddingLeft: '34px' }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search product or customer…"
            />
          </div>

          <select
            style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
            value={kindFilter}
            onChange={e => setKindFilter(e.target.value)}
          >
            <option value="">All Stock</option>
            <option value="open">Open only</option>
            <option value="locked">Reserved only</option>
          </select>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>Kind</th>
                <th style={thStyle}>Reserved for</th>
                <th style={thStyle}>Design</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Rolls</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Price</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && visible.map(row => {
                const locked = !!row.customer_id;
                const qty    = Number(row.quantity);
                const price  = Number(row.default_price || 0);

                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      {row.product_name}
                      <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '400' }}>
                        {row.tape_type_name}
                      </div>
                    </td>
                    <td style={tdStyle}><KindBadge locked={locked} /></td>
                    <td style={{ ...tdStyle, fontSize: '13px' }}>
                      {row.customer_name || <span style={{ color: C.textMuted }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '13px', color: C.textSoft }}>
                      {row.print_design || '—'}
                    </td>
                    <td style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '700',
                      color: locked ? C.purple : C.text,
                    }}>
                      {qty}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {editingPrice?.id === row.product_id ? (
                        <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="number"
                            autoFocus
                            defaultValue={editingPrice.price || ''}
                            onKeyDown={e => {
                              if (e.key === 'Enter')  savePrice(row.product_id, Number(e.target.value));
                              if (e.key === 'Escape') setEditingPrice(null);
                            }}
                            style={{
                              width: '80px', padding: '4px 6px', fontSize: '13px',
                              border: `1px solid ${C.border}`, borderRadius: '4px',
                              textAlign: 'right',
                            }}
                          />
                          <button
                            onClick={e => {
                              const input = e.target.closest('div').querySelector('input');
                              savePrice(row.product_id, Number(input.value));
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.success, display: 'flex' }}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => setEditingPrice(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex' }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <span
                          onClick={() => setEditingPrice({ id: row.product_id, price })}
                          title="Click to edit price"
                          style={{ cursor: 'pointer', borderBottom: `1px dashed ${C.border}` }}
                        >
                          {price > 0 ? money(price) : <span style={{ color: C.textMuted }}>set price</span>}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                      {price > 0 ? money(qty * price) : '—'}
                    </td>
                  </tr>
                );
              })}

              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    {term || kindFilter
                      ? 'Nothing matches that filter.'
                      : 'No finished stock yet. Record a production run to build stock.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Prices come from the product record and are not set
          anywhere yet — worth saying so rather than showing a
          silent zero. */}
      
    </div>
  );
};

export default FinishedStockTab;